import React, { useState } from 'react';
import { Button, Card, message, Popconfirm, Table, Tag, Input, Select, Space } from 'antd';
import { DeleteOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useGetAllTicketsQuery, useDeleteTicketMutation, useGetAllPoolsQuery } from './lotteryAPI';
import moment from 'moment';
import ContentWithErrorHandler from '../../app/ContentWithErrorHandler';
import { basicSort, TableColumnTextFilterConfig } from '../../common/TableHelper';

const LotteryTicketList: React.FC = () => {
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, error } = useGetAllTicketsQuery({ page: currentPage, pageSize });
  const { data: pools } = useGetAllPoolsQuery();
  const [deleteTicket] = useDeleteTicketMutation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const tickets = data?.data ?? [];
  const total = data?.total ?? 0;

  const handleDelete = async (ticketId: number) => {
    try {
      await deleteTicket(ticketId);
      message.success('Tiket berhasil dihapus');
    } catch (error) {
      message.error('Gagal menghapus tiket');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedRowKeys.map((id) => deleteTicket(id as number)));
      message.success(`${selectedRowKeys.length} tiket berhasil dihapus`);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('Gagal menghapus tiket');
    }
  };

  const downloadCsv = (filename: string, rows: Array<Array<string | number>>) => {
    const escapeValue = (value: string | number) => {
      const text = value == null ? '' : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const csvContent = rows.map((row) => row.map(escapeValue).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadCsv = () => {
    const rows = (tickets || []).map((ticket: any) => [
      ticket.ticketNumber,
      ticket.memberId,
      ticket.member?.name || '',
      ticket.pool?.name || '',
      ticket.category?.categoryId || '',
      ticket.category?.name || '',
      ticket.createdAt ? moment.utc(ticket.createdAt).add(14, 'hours').format('DD/MM/YYYY HH:mm') : '',
      ticket.notes || '',
    ]);

    downloadCsv('daftar-tiket-undian.csv', [
      ['Nomor Tiket', 'ID Anggota', 'Nama Anggota', 'Pool Undian', 'ID Kategori', 'Kategori', 'Tanggal Dibuat', 'Catatan'],
      ...rows,
    ]);
  };

  // Get unique categories from tickets
  const categories = React.useMemo(() => {
    if (!tickets) return [];
    const categoryMap = new Map();
    tickets.forEach((ticket: any) => {
      if (ticket.category) {
        categoryMap.set(ticket.category.id, ticket.category);
      }
    });
    return Array.from(categoryMap.values());
  }, [tickets]);

  const columns = [
    {
      title: 'Nomor Tiket',
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      render: (ticketNumber: string) => <Tag color="blue">{ticketNumber}</Tag>,
    },
    {
      title: 'ID Anggota',
      dataIndex: 'memberId',
      key: 'memberId',
      sorter: (a: any, b: any) => basicSort(a.memberId, b.memberId),
      ...TableColumnTextFilterConfig<any>('memberId'),
    },
    {
      title: 'Nama Anggota',
      dataIndex: ['member', 'name'],
      key: 'memberName',
      sorter: (a: any, b: any) => basicSort(a.member?.name, b.member?.name),
    },
    {
      title: 'Pool Undian',
      dataIndex: ['pool', 'name'],
      key: 'poolName',
      sorter: (a: any, b: any) => basicSort(a.pool?.name, b.pool?.name),
      filters: pools?.map((pool: any) => ({ text: pool.name, value: pool.name })),
      onFilter: (value: any, record: any) => record.pool?.name === value,
    },
    {
      title: 'ID Kategori',
      dataIndex: ['category', 'categoryId'],
      key: 'categoryCode',
      sorter: (a: any, b: any) => basicSort(a.category?.categoryId, b.category?.categoryId),
    },
    {
      title: 'Kategori',
      dataIndex: ['category', 'name'],
      key: 'categoryName',
      sorter: (a: any, b: any) => basicSort(a.category?.name, b.category?.name),
      filters: categories?.map((cat: any) => ({ text: cat.name, value: cat.name })),
      onFilter: (value: any, record: any) => record.category?.name === value,
    },
    {
      title: 'Tanggal Dibuat',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => moment.utc(createdAt).add(14, 'hours').format('DD/MM/YYYY HH:mm'),
      sorter: (a: any, b: any) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Catatan',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => notes || '-',
    },
    {
      title: 'Aksi',
      key: 'action',
      render: (_: any, record: any) => (
        <Popconfirm
          title="Apakah Anda yakin ingin menghapus tiket ini? Tindakan ini tidak dapat dibatalkan."
          onConfirm={() => handleDelete(record.id)}
          okText="Ya, Hapus"
          cancelText="Batal"
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <ContentWithErrorHandler isError={error} isLoading={false}>
      <Card
        title="Daftar Tiket Undian"
        size="small"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv} disabled={!tickets?.length}>
              Download CSV
            </Button>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`Apakah Anda yakin ingin menghapus ${selectedRowKeys.length} tiket? Tindakan ini tidak dapat dibatalkan.`}
                onConfirm={handleBulkDelete}
                okText="Ya, Hapus"
                cancelText="Batal"
              >
                <Button type="primary" danger icon={<DeleteOutlined />}>
                  Hapus {selectedRowKeys.length} Tiket
                </Button>
              </Popconfirm>
            )}
            <Button type="primary" icon={<PlusOutlined />}>
              <Link to="/lottery/assign-ticket" style={{ color: 'white' }}>
                Berikan Tiket
              </Link>
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={tickets}
          loading={isLoading}
          rowKey="id"
          size="small"
          bordered
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} tiket`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size && size !== pageSize) setPageSize(size);
            },
            onShowSizeChange: (page, size) => {
              setCurrentPage(1);
              setPageSize(size);
            },
          }}
        />
      </Card>
    </ContentWithErrorHandler>
  );
};

export default LotteryTicketList;
